import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { TRANSLATIONS } from '../i18n/portal';

// Minimal portal-scoped i18n. No external library — the surface is small
// (~150 keys) and we only need: a string lookup, a current-lang flag, and
// a setter. The Provider lives at PortalLayout level so /portal/* gets it
// but the rest of the app stays English.
//
// Storage:
//   • localStorage key `mpl-portal-lang` persists the choice across sessions
//     for instant UI flips next visit.
//   • PUT /api/portal/preferences/language persists the choice on the
//     division doc so the AI review backend can read it (the model uses
//     this to decide whether to write its observation/recommendation in
//     Hindi or English).
//
// API:
//   const { t, lang, setLang } = usePortalI18n();
//   t('claims.title')                       → 'My Claims' or 'मेरे दावे'
//   t('dash.welcome', { name: 'Sumit' })    → interpolation via {name}
//   prettifyDocType(slug) is centralised here too so it can be Hindi-aware.

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LS_KEY = 'mpl-portal-lang';
const DEFAULT_LANG = 'en';
const SUPPORTED = new Set(['en', 'hi']);

const PortalI18nContext = createContext(null);

const interpolate = (template, vars) => {
  if (!vars || !template) return template;
  return template.replace(/\{(\w+)\}/g, (_, k) =>
    (vars[k] !== undefined && vars[k] !== null) ? String(vars[k]) : `{${k}}`
  );
};

// Lookup the value of a translation key. We always have the English dict as
// the fallback ground truth — keeps the UI usable even if Hindi is missing
// the odd key.
const lookup = (lang, key) => {
  const primary = TRANSLATIONS[lang] && TRANSLATIONS[lang][key];
  if (primary != null && primary !== '') return primary;
  const en = TRANSLATIONS.en && TRANSLATIONS.en[key];
  return en != null ? en : key;
};

export const PortalI18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    try {
      const stored = localStorage.getItem(LS_KEY);
      if (stored && SUPPORTED.has(stored)) return stored;
    } catch (_) {
      // localStorage can throw in private browsing — fall back to default.
    }
    return DEFAULT_LANG;
  });

  // Persist + reflect on <html lang="…"> so screen readers / font hints kick in.
  useEffect(() => {
    try { localStorage.setItem(LS_KEY, lang); } catch (_) {
      // Same private-browsing scenario — non-fatal.
    }
    if (typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.setAttribute('lang', lang);
    }
  }, [lang]);

  const setLang = useCallback((l) => {
    if (!SUPPORTED.has(l)) return;
    setLangState(l);
    // Persist the choice on the division doc so the backend AI review
    // picks it up. Fire-and-forget — UI has already flipped via the
    // useState update; if the backend write fails (offline, 401 during
    // session refresh) the next page load will retry through whichever
    // flow next mutates the division. Silently swallowing the error
    // here is correct.
    axios
      .put(
        `${API_BASE}/portal/preferences/language`,
        { lang: l },
        { withCredentials: true, timeout: 8000 },
      )
      .catch(() => { /* non-fatal */ });
  }, []);

  const t = useCallback((key, vars) => interpolate(lookup(lang, key), vars), [lang]);

  // Pretty doc-type label. Looks up `doctype.<slug>` first; falls back to
  // title-cased slug if no translation exists. Means custom admin slugs
  // like `extra_form` render as "Extra Form" cleanly.
  const tDocType = useCallback((slug) => {
    if (!slug) return '';
    const key = `doctype.${String(slug).trim()}`;
    const hi = TRANSLATIONS[lang] && TRANSLATIONS[lang][key];
    if (hi) return hi;
    const en = TRANSLATIONS.en[key];
    if (en) return en;
    return String(slug).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }, [lang]);

  // Status name (Draft, Submitted etc.) — short helper so call sites don't
  // have to manually pass status keys.
  const tStatus = useCallback((s) => t(`status.${s}`) || s, [t]);

  const value = useMemo(() => ({ lang, setLang, t, tDocType, tStatus }), [lang, setLang, t, tDocType, tStatus]);

  return (
    <PortalI18nContext.Provider value={value}>
      {children}
    </PortalI18nContext.Provider>
  );
};

export const usePortalI18n = () => {
  const ctx = useContext(PortalI18nContext);
  if (!ctx) {
    // Defensive fallback so a component used outside the provider
    // still renders English without crashing — the portal is the only
    // place we expect this hook to be used, but components like
    // RequiredDocsUploader can be reused elsewhere too.
    return {
      lang: 'en',
      setLang: () => {},
      t: (k, vars) => interpolate(lookup('en', k), vars),
      tDocType: (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      tStatus: (s) => lookup('en', `status.${s}`) || s,
    };
  }
  return ctx;
};
